import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { Reveal } from './Reveal.tsx';
import type { SiteConfig } from '../../siteContentTypes.ts';

function embedUrl(url: string, type: SiteConfig['video']['type']): string | null {
  if (!url || url === 'VIDEO_URL_HERE') return null;
  if (type === 'youtube') {
    const idMatch = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
    const id = idMatch ? idMatch[1] : url;
    return `https://www.youtube.com/embed/${id}?autoplay=1`;
  }
  if (type === 'vimeo') {
    const idMatch = url.match(/vimeo\.com\/(\d+)/);
    const id = idMatch ? idMatch[1] : url;
    return `https://player.vimeo.com/video/${id}?autoplay=1`;
  }
  return url;
}

export function VideoSection({ config }: { config: SiteConfig }) {
  const [open, setOpen] = useState(false);
  const video = config.video;
  const src = embedUrl(video.url, video.type);

  return (
    <section style={{ padding: '56px 24px', maxWidth: 1000, margin: '0 auto' }}>
      <Reveal>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 10px' }}>{video.title}</h2>
          {video.subtitle && <p style={{ color: 'var(--text-secondary)', fontSize: 15 }}>{video.subtitle}</p>}
        </div>

        <button
          type="button"
          onClick={() => src && setOpen(true)}
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            borderRadius: 24,
            border: 'none',
            cursor: src ? 'pointer' : 'default',
            padding: 0,
            overflow: 'hidden',
            background: video.thumbnail
              ? `center/cover no-repeat url(${video.thumbnail})`
              : 'linear-gradient(135deg, #2a2a28, #171717)',
          }}
          className="video-thumb"
          aria-label={video.buttonText}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
              }}
              className="video-play-button"
            >
              <Play size={30} color="var(--accent-dark)" fill="var(--accent-dark)" style={{ marginLeft: 4 }} />
            </div>
          </div>
          {!src && (
            <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 12, fontWeight: 700 }}>
              VIDEO_URL_HERE — à remplacer depuis le back-office
            </div>
          )}
        </button>
      </Reveal>

      <AnimatePresence>
        {open && src && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(10,10,10,0.85)',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '100%', maxWidth: 960, aspectRatio: '16 / 9', position: 'relative' }}
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                style={{
                  position: 'absolute',
                  top: -40,
                  right: 0,
                  background: 'none',
                  border: 'none',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                <X size={26} />
              </button>
              {video.type === 'mp4' ? (
                <video src={src} controls autoPlay style={{ width: '100%', height: '100%', borderRadius: 12 }} />
              ) : (
                <iframe
                  src={src}
                  title={video.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  style={{ width: '100%', height: '100%', border: 'none', borderRadius: 12 }}
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
